import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetIntegrationUsersQuery } from '../../../store/integrations/integrations'
import { useMeetingUsers } from '../../../apps/professional-app/fa/MeetingSummary/hooks/users'
import { selectRoomSession } from '../../../store/meetingSummary/roomSession'
import { getContactDropdownComponent } from '../ContactDropdownItems'
import { type IntegrationServiceId } from '../../../common/integrations'
import { getContactOptions } from './utils'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../common/errorMessages'
import { useContactLabel } from './V2/hooks'
import { type User } from '../../../models/User'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

export interface ContactDropdownProps {
  serviceId: IntegrationServiceId
  onContactOptionSelected?: (option: SelectOption) => void
  onContactSelected?: (user: User) => void
  fieldName?: string
  contactOptions?: SelectOption[]
  onSearchInputChange?: (searchInput: string) => Promise<void>
  initialSelectedOptionExternalId?: string
  showLabel?: boolean
  isLoading?: boolean
  prefetchContacts?: boolean
}

interface StandaloneDropdownContentProps {
  serviceId: IntegrationServiceId
  contactLabel: string
  onContactOptionSelected?: (option: SelectOption) => void
  onContactSelected?: (user: User) => void
  initialSelectedOptionExternalId?: string
  showLabel: boolean
  isLoading: boolean
  prefetchContacts: boolean
}

const StandaloneDropdownContent: React.FC<StandaloneDropdownContentProps> = ({
  serviceId,
  contactLabel,
  onContactOptionSelected,
  onContactSelected,
  initialSelectedOptionExternalId,
  showLabel,
  isLoading,
  prefetchContacts,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const [searchInput, setSearchInput] = useState<string | null>(null)
  const searchFilters = useMemo(() => {
    if (searchInput !== null) {
      if (!searchInput && prefetchContacts) {
        return {}
      }
      return { name: searchInput }
    }
    if (prefetchContacts) {
      return {}
    }
    return undefined
  }, [searchInput, prefetchContacts])

  const {
    data: initialContactData,
    isError: isInitialError,
    error: initialError,
    isLoading: isInitialQueryLoading,
    isFetching: isInitialFetching,
  } = useGetIntegrationUsersQuery(
    { accountId, connector: serviceId, filters: { externalIds: [initialSelectedOptionExternalId ?? ''] } },
    { skip: !accountId || !serviceId || !initialSelectedOptionExternalId },
  )

  const {
    data: searchContactsData,
    isError: isSearchError,
    error: searchError,
    isFetching: isSearchFetching,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId, filters: searchFilters ?? {} }, { skip: !accountId || !serviceId || !searchFilters })

  const contactOptions = useMemo(() => {
    const data = searchContactsData?.data ?? initialContactData?.data
    if (!data) {
      return []
    }
    return getContactOptions(data, serviceId)
  }, [searchContactsData, initialContactData, serviceId])

  const initialOption = useMemo(() => {
    if (!initialSelectedOptionExternalId) {
      return null
    }
    if (initialContactData?.data) {
      const options = getContactOptions(initialContactData.data, serviceId)
      const found = options.find((option) => option.id === initialSelectedOptionExternalId)
      if (found) {
        return found
      }
    }
    if (!isInitialQueryLoading && !isInitialFetching) {
      return { id: initialSelectedOptionExternalId, label: initialSelectedOptionExternalId }
    }
    return null
  }, [initialSelectedOptionExternalId, initialContactData, serviceId, isInitialQueryLoading, isInitialFetching])

  const getUserFromOption = useCallback(
    (option: SelectOption) => {
      const user = searchContactsData?.data?.find((u) => u.integrations?.find((i) => i.serviceId === serviceId)?.externalId === option.id)
      if (user) {
        return user
      }
      return initialContactData?.data?.find((u) => u.integrations?.find((i) => i.serviceId === serviceId)?.externalId === option.id) ?? null
    },
    [searchContactsData, initialContactData, serviceId],
  )

  useEffect(() => {
    if (initialOption) {
      onContactOptionSelected?.(initialOption)
      const user = getUserFromOption(initialOption)
      if (user) {
        onContactSelected?.(user)
      }
    }
  }, [initialOption, onContactOptionSelected, onContactSelected, getUserFromOption])

  const handleSearchInputChange = useCallback(async (input: string) => {
    setSearchInput(input)
  }, [])

  const isInitialLoading = !!initialSelectedOptionExternalId && !initialOption && (isInitialQueryLoading || isInitialFetching)
  const isError = isInitialError || isSearchError
  const error = initialError || searchError
  const isInvalidIntegration = isError && INVALID_INTEGRATION_ERROR_MESSAGES.some((msg) => JSON.stringify(error).match(msg))

  return (
    <>
      {isInitialLoading && (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      )}
      {!isInitialLoading && isInvalidIntegration && (
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
      )}
      {!isInitialLoading && !isInvalidIntegration && (
        <div>
          {showLabel && <div className='lead-extra-small'>{contactLabel}</div>}
          <SelectDropdown
            initialSelectedOption={initialOption}
            options={contactOptions}
            className='py-2 zocks-px-0.75'
            onOptionSelected={(option: SelectOption) => {
              onContactOptionSelected?.(option)
              const user = getUserFromOption(option)
              if (user) {
                onContactSelected?.(user)
              }
            }}
            DropDownItemOptionComponent={getContactDropdownComponent(serviceId)}
            searchable
            onSearchInputChange={handleSearchInputChange}
            placeholder={`Select your ${contactLabel}`}
            loading={isSearchFetching || isInitialFetching || isLoading}
          />
        </div>
      )}
    </>
  )
}

interface FormDropdownContentProps {
  serviceId: IntegrationServiceId
  contactLabel: string
  fieldName: string
  contactOptions: SelectOption[]
  onSearchInputChange?: (searchInput: string) => Promise<void>
  onContactOptionSelected?: (option: SelectOption) => void
  onContactSelected?: (user: User) => void
  showLabel: boolean
  isLoading: boolean
}

const FormDropdownContent: React.FC<FormDropdownContentProps> = ({
  serviceId,
  contactLabel,
  fieldName,
  contactOptions: externalContactOptions,
  onSearchInputChange,
  onContactOptionSelected,
  onContactSelected,
  showLabel,
  isLoading,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { control, setValue } = useFormContext()
  const roomSession = useAppSelector(selectRoomSession)
  const { usersByRoles } = useMeetingUsers(roomSession)

  const integration = useMemo(() => {
    if (!usersByRoles) {
      return null
    }

    const clients = usersByRoles['Clients']

    if (!clients || clients.length !== 1) {
      return null
    }

    return clients[0].integrations?.find((i) => i.serviceId === serviceId) || null
  }, [usersByRoles, serviceId])

  const { isLoading: isContactsLoading, data: contactsData } = useGetIntegrationUsersQuery(
    {
      accountId,
      connector: serviceId,
      filters: integration?.externalId ? { externalIds: [integration?.externalId] } : {},
    },
    { skip: !accountId || !serviceId || !integration },
  )

  const suggestedContact = useMemo(() => {
    if (!contactsData?.data) {
      return null
    }
    const options = getContactOptions(contactsData.data, serviceId)
    return options[0] || null
  }, [contactsData, serviceId])

  useEffect(() => {
    if (!suggestedContact?.id) {
      return
    }

    setValue(fieldName, suggestedContact.id)
  }, [suggestedContact, setValue, fieldName])

  const getUserFromOption = useCallback(
    (option: SelectOption) => contactsData?.data?.find((u) => u.integrations?.find((i) => i.serviceId === serviceId)?.externalId === option.id) ?? null,
    [contactsData, serviceId],
  )

  const dropdownOptions = useMemo(() => {
    const options = [...externalContactOptions]
    if (suggestedContact && !options.find((option) => option.id === suggestedContact.id)) {
      options.push(suggestedContact)
    }
    return options
  }, [externalContactOptions, suggestedContact])

  return (
    <>
      {showLabel && <div className='lead-extra-small'>{contactLabel}</div>}
      <Controller
        name={fieldName}
        control={control}
        rules={{ required: `${contactLabel} is required.` }}
        render={({ field, fieldState }) => (
          <>
            <SelectDropdown
              initialSelectedOption={suggestedContact}
              options={dropdownOptions}
              className='py-2 zocks-px-0.75'
              onOptionSelected={(option: SelectOption) => {
                field.onChange(option.id)
                onContactOptionSelected?.(option)
                const user = getUserFromOption(option)
                if (user) {
                  onContactSelected?.(user)
                }
              }}
              DropDownItemOptionComponent={getContactDropdownComponent(serviceId)}
              searchable
              onSearchInputChange={onSearchInputChange}
              placeholder={`Select your ${contactLabel}`}
              loading={isLoading || isContactsLoading}
            />
            {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
          </>
        )}
      />
    </>
  )
}

export const ContactDropdown: React.FC<ContactDropdownProps> = ({
  serviceId,
  onContactOptionSelected,
  onContactSelected,
  fieldName,
  contactOptions = [],
  onSearchInputChange,
  initialSelectedOptionExternalId,
  showLabel = true,
  isLoading = false,
  prefetchContacts = false,
}) => {
  const { contactLabel } = useContactLabel(serviceId)

  if (fieldName) {
    return (
      <FormDropdownContent
        serviceId={serviceId}
        contactLabel={contactLabel}
        fieldName={fieldName}
        contactOptions={contactOptions}
        onSearchInputChange={onSearchInputChange}
        onContactOptionSelected={onContactOptionSelected}
        onContactSelected={onContactSelected}
        showLabel={showLabel}
        isLoading={isLoading}
      />
    )
  }

  return (
    <StandaloneDropdownContent
      serviceId={serviceId}
      contactLabel={contactLabel}
      onContactOptionSelected={onContactOptionSelected}
      onContactSelected={onContactSelected}
      initialSelectedOptionExternalId={initialSelectedOptionExternalId}
      showLabel={showLabel}
      isLoading={isLoading}
      prefetchContacts={prefetchContacts}
    />
  )
}
